import analytics, { isTrackedMarketingProduct, shouldTrackMarketingEvent } from '.';
import { getTenancy } from '../../hooks/useTenancy';
import { hasTargetingConsent } from '../consent';
import { getProductsAndVariantsBySku } from '../product';
import store from '../reduxStore';
import { typedWindow } from '../Window';
import { ECOM_EVENT_ORDER_COMPLETED, SegmentPurchaseEventProperties } from './events';

/**
 * Vibe (vibe.co) pixel integration for the Ultra TV ad campaign. The base loader (init +
 * initial page_view) lives in public/index.html, gated on OneTrust targeting consent (C0004);
 * this module fires purchase/lead conversions and SPA page views. All handlers no-op unless
 * production, non-test tenancy, targeting consent, and a loaded pixel hold.
 *
 * Event shapes match the dashboard's "Additional events" snippets:
 *   vbpx('event', 'purchase', { price_usd, purchase_id })  // USD only
 *   vbpx('event', 'lead')
 *
 * @see https://help.vibe.co/en/articles/6530668-vibe-pixel
 */

function shouldFireVibeEvent(): boolean {
  return (
    getTenancy() !== 'test' &&
    process.env.REACT_APP_ENVIRONMENT === 'production' &&
    hasTargetingConsent() &&
    typeof typedWindow.vbpx === 'function'
  );
}

/** Tracked collar + subscription value, matching the other marketing pixels (not order total). */
function getTrackedProductsValue(properties: SegmentPurchaseEventProperties): number {
  const productsAndVariantsBySku = getProductsAndVariantsBySku(store.getState().config.products);

  const trackedProducts = properties.products.filter((product) =>
    isTrackedMarketingProduct(product, productsAndVariantsBySku),
  );

  return trackedProducts.reduce((total, product) => {
    const subscriptionValue = product.analyticsSubscriptionValue ?? product.subscriptionPrice ?? 0;
    const productValue = Number(product.price) + Number(subscriptionValue);
    return total + productValue * product.quantity;
  }, 0);
}

function vibeOrderCompleted(_messageId: string, properties: SegmentPurchaseEventProperties) {
  // USD-only: Vibe has no currency param, so skip non-USD orders rather than mislabel them.
  if (!shouldFireVibeEvent() || properties.currency !== 'USD') {
    return;
  }

  typedWindow.vbpx?.('event', 'purchase', {
    price_usd: getTrackedProductsValue(properties),
    purchase_id: properties.orderId,
  });
}

// Map Segment events to Vibe handlers
type VibeEventHandler = (messageId: string, properties: any) => void;
const VIBE_EVENT_MAP: Record<string, VibeEventHandler> = {
  [ECOM_EVENT_ORDER_COMPLETED]: vibeOrderCompleted,
};

function onTrack(event: string, properties: any, messageId: string) {
  const handler = VIBE_EVENT_MAP[event];
  if (!handler || !shouldTrackMarketingEvent(properties)) {
    return;
  }
  handler(messageId, properties);
}

/** Lead event on checkout email capture, alongside the other pixels' email-capture hooks. */
export function trackVibeLead() {
  if (!shouldFireVibeEvent()) {
    return;
  }
  typedWindow.vbpx?.('event', 'lead');
}

/** page_view on SPA navigation; the initial view fires from the base loader in index.html. */
export function vibeTrackPageView() {
  if (!shouldFireVibeEvent()) {
    return;
  }
  typedWindow.vbpx?.('event', 'page_view');
}

/** Wire Segment events to Vibe handlers; each handler re-checks consent, so this is safe pre-consent. */
export function vibeSetupCallbacks() {
  analytics.onTrack(onTrack);
}
